/**
 * Adobe Analytics Client
 * Core methods to trigger Adobe Analytics events via window.s object
 *
 * Note: This module relies on window.s being available at runtime.
 * Each consuming app should declare their own Window interface extension.
 */

import type { CustomLinkType } from "../types";
import type { AdobeAnalyticsMethods } from "./types";

/**
 * Check if application is running in development/local mode
 */
const isDevelopment = (): boolean => {
  try {
    // Check for Vite development mode
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mode = (import.meta as any)?.env?.MODE;
    if (mode) {
      return mode === "development";
    } else {
      return (
        typeof window !== "undefined" &&
        (window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1")
      );
    }
  } catch {
    // Fallback: check if running on localhost
    return (
      typeof window !== "undefined" &&
      (window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1")
    );
  }
};

/**
 * Log analytics event to console in development mode
 */
const logAnalyticsEvent = (
  methodName: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params: Record<string, any>,
): void => {
  if (isDevelopment()) {
    console.log(
      `%c[Adobe Analytics] ${methodName}`,
      "color: #6366f1; font-weight: bold;",
      params,
    );
  }
};

/**
 * Type-safe access to window.s
 * Returns undefined if window.s is not available
 */
const getAnalytics = (): AdobeAnalyticsMethods | undefined => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (window as any).s as AdobeAnalyticsMethods | undefined;
};

/**
 * Track form workflow lifecycle events
 * @param formName - Name of the form being tracked
 * @param action - Form action (initiated, step, completed, submitted, error)
 * @param stepNumber - Step number for multi-step forms (default: '1')
 * @param pageLoad - Whether action occurred on page load (default: 'false')
 * @param errorMessage - Error message if action is 'error' (default: '')
 */
export const trackFormWorkflow = (
  formName: string,
  action: string,
  stepNumber: string = "1",
  pageLoad: string = "false",
  errorMessage: string = "",
): void => {
  logAnalyticsEvent("pfFormWorkflow", {
    formName,
    action,
    stepNumber,
    pageLoad,
    errorMessage,
  });

  const analytics = getAnalytics();
  if (analytics && typeof analytics.pfFormWorkflow === "function") {
    analytics.pfFormWorkflow(
      formName,
      action,
      stepNumber,
      pageLoad,
      errorMessage,
    );
  }
};

/**
 * Track form input field values
 * @param formName - Name of the form being tracked
 * @param stepNumber - Step number for the form field
 * @param formQuestion - Question/label for the form field
 * @param formAnswer - User's answer (use empty string for PII)
 * @param questionIndex - Index of the question in the form
 */
export const trackFormInput = (
  formName: string,
  stepNumber: string,
  formQuestion: string,
  formAnswer: string,
  questionIndex: string,
): void => {
  logAnalyticsEvent("pfFormWorkflowInput", {
    formName,
    stepNumber,
    formQuestion,
    formAnswer,
    questionIndex,
  });

  const analytics = getAnalytics();
  if (analytics && typeof analytics.pfFormWorkflowInput === "function") {
    analytics.pfFormWorkflowInput(
      formName,
      stepNumber,
      formQuestion,
      formAnswer,
      questionIndex,
    );
  }
};

/**
 * Track content liked by user
 * @param contentTitle - Title of the liked content
 * @param contentType - Type of content (page, article, document, video, etc.)
 * @param contentPlacement - Optional placement/location on page
 */
export const trackContentLiked = (
  contentTitle: string,
  contentType: string,
  contentPlacement: string = "",
): void => {
  logAnalyticsEvent("pfContentLiked", {
    contentTitle,
    contentType,
    contentPlacement,
  });

  const analytics = getAnalytics();
  if (analytics && typeof analytics.pfContentLiked === "function") {
    analytics.pfContentLiked(contentTitle, contentType, contentPlacement);
  }
};

/**
 * Track content disliked by user
 * @param contentTitle - Title of the disliked content
 * @param contentType - Type of content (page, article, document, video, etc.)
 * @param contentPlacement - Optional placement/location on page
 */
export const trackContentDisliked = (
  contentTitle: string,
  contentType: string,
  contentPlacement: string = "",
): void => {
  logAnalyticsEvent("pfContentDisliked", {
    contentTitle,
    contentType,
    contentPlacement,
  });

  const analytics = getAnalytics();
  if (analytics && typeof analytics.pfContentDisliked === "function") {
    analytics.pfContentDisliked(contentTitle, contentType, contentPlacement);
  }
};

/**
 * Track custom link/feature usage via custom event
 * @param featureName - Name of the feature being tracked
 * @param linkType - Type of link ('internal' or 'external')
 */
export const trackCustomLink = (
  featureName: string,
  linkType: CustomLinkType,
): void => {
  logAnalyticsEvent("pfLinkName", {
    featureName,
    linkType,
  });

  const dataLayer: Array<{
    event: string;
    pfLinkName: { linkName: string; linkType: CustomLinkType };
  }> = [];
  dataLayer.push({
    event: "pfLinkName",
    pfLinkName: { linkName: featureName, linkType },
  });
  const event = new CustomEvent("pfAnalytics", { detail: dataLayer });
  document.querySelector("body")?.dispatchEvent(event);
};

/**
 * Track file download events
 * @param fileName - Name of the downloaded file
 * @param folderName - Optional folder path (folder1/folder2/folder3)
 * @param fileDownloadTitle - Optional download title
 */
export const trackDownload = (
  fileName: string,
  folderName: string = "",
  fileDownloadTitle: string = "",
): void => {
  logAnalyticsEvent("pfFileDownload", {
    fileName,
    folderName,
    fileDownloadTitle,
  });

  const analytics = getAnalytics();
  if (analytics && typeof analytics.pfFileDownload === "function") {
    analytics.pfFileDownload(fileName, folderName, fileDownloadTitle);
  }
};
